import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { dealsApi } from "@/api/endpoints";
import type { DealDetail } from "@/api/types";
import { CustomerPreview } from "@/components/CustomerPreview";
import { Ribbon } from "@/components/layout/Ribbon";
import { Note } from "@/components/ui/Note";
import { StepRail } from "@/components/wizard/StepRail";
import { useAuth } from "@/auth/use-auth";
import { useI18n } from "@/i18n/use-i18n";
import { ActivationStep } from "@/pages/steps/ActivationStep";
import { ContractStep } from "@/pages/steps/ContractStep";
import { CustomerStep } from "@/pages/steps/CustomerStep";
import { PaymentStep } from "@/pages/steps/PaymentStep";
import { ProductStep } from "@/pages/steps/ProductStep";
import { SignatureStep } from "@/pages/steps/SignatureStep";

export function DealPage() {
  const { dealId } = useParams<{ dealId: string }>();
  const { t } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [step, setStep] = useState(0);
  const [previewOpen, setPreviewOpen] = useState(false);

  const dealQuery = useQuery({
    queryKey: ["deal", dealId],
    queryFn: () => dealsApi.detail(dealId!),
    enabled: Boolean(dealId),
  });

  const deal = dealQuery.data;

  // Resume where the deal was left, but never on a step it has since outgrown.
  useEffect(() => {
    if (deal) setStep(Math.min(deal.current_step, deal.reached_step));
  }, [deal?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const createDeal = useMutation({
    mutationFn: dealsApi.create,
    onSuccess: (created) => {
      void queryClient.invalidateQueries({ queryKey: ["deals"] });
      void queryClient.invalidateQueries({ queryKey: ["deal-stats"] });
      navigate(`/vorgang/${created.id}`);
    },
  });

  /** Replace the cached deal after a mutation returns a fresh copy. */
  function applyDeal(updated: DealDetail) {
    queryClient.setQueryData(["deal", updated.id], updated);
    void queryClient.invalidateQueries({ queryKey: ["deals"] });
    void queryClient.invalidateQueries({ queryKey: ["deal-stats"] });
  }

  /** Move to a step and remember it server-side, ignoring persistence failures. */
  function goToStep(next: number) {
    setStep(next);
    window.scrollTo({ top: 0, behavior: "smooth" });
    if (!deal) return;

    // Deliberately NOT gated on `deal.reached_step`. When a step is advanced by
    // the mutation that unlocked it - "Paket freigeben" calling onNext - `deal`
    // here is still the pre-mutation copy from this render, so its reached_step
    // is one behind and the gate silently skipped the write. The step then never
    // persisted, and a reload dropped the user back to the previous step while
    // the ribbon showed the signature already sent.
    //
    // The backend is the authority: it rejects an illegal step with a 400, which
    // is swallowed here because the UI has already moved and the only cost is a
    // stale resume position.
    dealsApi.setStep(deal.id, next).then(applyDeal).catch(() => undefined);
  }

  if (dealQuery.isPending) {
    return (
      <p className="page-shell py-10 text-muted">{t("deal.loading")}</p>
    );
  }

  if (dealQuery.isError || !deal) {
    return (
      <div className="page-shell py-10">
        <Note tone="error">
          {(dealQuery.error as Error | null)?.message ?? t("deal.notFound")}
        </Note>
      </div>
    );
  }

  // Everyone may open any Vorgang, but only whoever started it may change it -
  // the backend enforces the same rule, this just stops the UI offering buttons
  // that would come back 403.
  const readOnly = deal.owner.id !== user?.id && user?.role !== "geschaeftsfuehrung";

  const shared = {
    deal,
    readOnly,
    onChanged: applyDeal,
    onNext: () => goToStep(step + 1),
    onBack: () => goToStep(step - 1),
    onOpenPreview: () => setPreviewOpen(true),
  };

  return (
    <>
      <Ribbon deal={deal} />

      {readOnly && (
        <div className="page-shell pt-5">
          <Note tone="info">{t("deal.readOnly", { name: deal.owner.name })}</Note>
        </div>
      )}

      <div className="page-shell flex flex-col items-stretch gap-6.5 pt-6.5 pb-17 lg:flex-row lg:items-start">
        <StepRail current={step} reached={deal.reached_step} onSelect={goToStep} />

        <main className="min-w-0 flex-1">
          {step === 0 && <ProductStep {...shared} />}
          {step === 1 && <CustomerStep {...shared} />}
          {step === 2 && <ContractStep {...shared} />}
          {step === 3 && <SignatureStep {...shared} />}
          {step === 4 && <PaymentStep {...shared} />}
          {step === 5 && (
            <ActivationStep
              deal={deal}
              readOnly={readOnly}
              onChanged={applyDeal}
              onBack={shared.onBack}
              onOpenPreview={shared.onOpenPreview}
              onStartNewDeal={() => createDeal.mutate()}
              startingNewDeal={createDeal.isPending}
            />
          )}
        </main>
      </div>

      {previewOpen && deal.product && (
        <CustomerPreview
          productName={deal.product.name}
          scenes={deal.product.demo_scenes}
          onClose={() => setPreviewOpen(false)}
        />
      )}
    </>
  );
}
